import React, {
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useContext,
} from "react";

import { Text, View, StyleSheet, TouchableWithoutFeedback } from "react-native";
import { GiftedChat } from "react-native-gifted-chat";
import InputToolBar from "./InputToolBar";
import Bubble from "./Bubble";
import CallBubble from "./CallBubble";
import SystemMessage, { SystemText } from "./SystemMessage";
import palette from "~/styles/palette";
import { useHistory } from "react-router-dom";
import ConversationContext from "../../contexts/ConversationContext";
import AppContext from "../../contexts/AppContext";
import IconButton from "~/components/Buttons/IconButton";
import RecipientSearchInput from "../RecipientSearchInput/index.web";
import Icon from "react-native-vector-icons/Feather";
import Row from "../../layouts/Row";
import Col from "../../layouts/Col";
import UserContext from "../../contexts/UserContext";
import Avatar from "../Avatar/index";
import TwilioContext from "../../contexts/TwilioContext";
import ReactAudioPlayer from "react-audio-player";
import dayjs from "~/helpers/day";

export default function ConversationThread() {
  const { user } = useContext(UserContext);
  const { conversation, sendMessage } = useContext(ConversationContext);

  let inputRef = useRef();
  let giftedRef = useRef();
  let initialScroll = useRef(false);

  const onEnterPress = useCallback(
    (e) => {
      if (!e.nativeEvent.shiftKey && e.nativeEvent.key === "Enter") {
        onSend([
          {
            conversation_id: conversation.id,
            text: inputRef.current.value,
            user: {
              ...user,
              _id: conversation?.twilio_number?.id,
            },
            createdAt: new Date(),
          },
        ]);
        return true;
      }
    },
    [user, onSend, conversation]
  );

  const onSend = useCallback(
    async (messages = []) => {
      if (messages && messages.length) {
        const msg = messages[0];
        if (msg.text) {
          sendMessage({ ...msg, body: msg.text });
          if (giftedRef.current) giftedRef.current.scrollToBottom();
        }
      }
    },
    [sendMessage]
  );
  // End Handlers for GiftedChat component

  useEffect(() => {
    if (!initialScroll.current) {
      setTimeout(() => {
        if (giftedRef.current) {
          giftedRef.current.scrollToBottom();
          initialScroll.current = true;
        }
      }, 600);
    }
  }, [conversation?.interactions?.length]);

  const interactions = useMemo(() => {
    return conversation?.interactions.map((i) => {
      const { author, ...interaction } = i;
      return {
        user: author,
        ...interaction,
      };
    });
  }, [conversation?.interactions]);

  return (
    <>
      {conversation?.id ? <ConversationBar /> : <NewConversationBar />}
      <View style={{ width: "100%", flex: 1, zIndex: -1 }}>
        <GiftedChat
          ref={(ref) => (giftedRef.current = ref)}
          minInputToolbarHeight={86}
          minComposerHeight={120}
          maxComposerHeight={120}
          listViewProps={{
            showsVerticalScrollIndicator: false,
          }}
          scrollToBottom
          textInputStyle={styles.conversation_thread__text_input}
          messages={interactions}
          onSend={onSend}
          textInputProps={{
            onKeyPress: onEnterPress,
            innerRef: inputRef,
          }}
          inverted={false}
          renderInputToolbar={renderInputToolbar}
          renderBubble={renderBubble}
          renderAccessory={renderAccessory}
          renderSend={renderSend}
          renderSystemMessage={renderSystemMessage}
          renderAvatar={renderAvatar}
          user={{ ...user, _id: conversation?.twilio_number?.id }}
        />
      </View>
    </>
  );
}

const NewConversationBar = () => {
  return (
    <View style={[styles.new_conversation_bar__container]}>
      <Row>
        <Col maxWidth={50} leftCenter pl={16}>
          <Text style={[styles.new_conversation_bar__input_label]}>To</Text>
        </Col>
        <Col>
          <RecipientSearchInput />
        </Col>
      </Row>
    </View>
  );
};

const ConversationBar = () => {
  const history = useHistory();
  const { conversation } = useContext(ConversationContext);
  const { compactMode } = useContext(AppContext);
  const { activeCall, startCall } = useContext(TwilioContext);

  const title =
    conversation?.participant?.name || conversation?.participant?.phone_number;

  return (
    <View style={[styles.conversation_bar__container]}>
      <Row pl={15} pr={15} style={{ height: "100%" }}>
        {compactMode && (
          <Col left maxWidth={75}>
            <View
              style={[styles.conversation_bar__back_btn_text_container]}
              onClick={history.goBack}
            >
              <Icon
                name="chevron-left"
                color={palette.success}
                size={32}
                style={[styles.conversation_bar__back_btn_icon]}
              />
              <Text style={[styles.conversation_bar__back_btn_text]}>Back</Text>
            </View>
          </Col>
        )}
        <Col center>
          <Text style={[styles.conversation_bar__title_text]}>{title}</Text>
        </Col>
        <Col maxWidth={75} pr={8}>
          <View
            style={[styles.conversation_bar__back_btn_text_container]}
            onClick={history.goBack}
          >
            {/* <IconButton
              iconName="info"
              iconColor={palette.black}
              color={palette.lighter_grey}
              iconSize={18}
            /> */}
            {/* <IconButton
              iconName="minimize-2"
              iconColor={palette.black}
              color={palette.lighter_grey}
              iconSize={18}
            /> */}
            <IconButton
              iconName="phone"
              onPress={() =>
                startCall(
                  conversation?.participant?.phone_number,
                  conversation?.participant
                )
              }
              iconColor={palette.white}
              color={palette.success}
              iconSize={18}
              disabled={!!activeCall}
            />
          </View>
        </Col>
      </Row>
    </View>
  );
};

const renderAvatar = (props) => {
  const { user } = props.currentMessage;
  return <Avatar size={40} initials={user?.initials} />;
};

const renderAccessory = (props) => {
  const { onSend, text } = props;
  return (
    <Row
      center
      pt={5}
      pb={5}
      style={{
        height: 50,
        borderBottomColor: palette.light_grey,
        borderBottomWidth: 1,
        display: "flex",
      }}
    >
      <Row pl={16} pr={16} pt={8} pb={8}>
        <Icon name="image" size={24} iconSize={18} ml={0} />
      </Row>
    </Row>
  );
};

const renderSend = (props) => {
  const { onSend, text } = props;

  const onSendHandler = () => {
    if (text) {
      onSend({ text: text.trim() }, true);
    }
  };
  return (
    <View
      style={{
        height: 120,
      }}
    >
      <IconButton
        onPress={onSendHandler}
        iconName="send"
        iconSize={30}
        height={40}
        ml={0}
        iconColor={text ? palette.primary_light : palette.light_grey}
      />
      {/* <IconButton
        onPress={onSendHandler}
        iconName="paperclip"
        iconSize={20}
        ml={0}
        iconColor={palette.black}
      /> */}
    </View>
  );
};

const renderInputToolbar = (props) => {
  return (
    <InputToolBar
      {...props}
      containerStyle={{
        borderTopWidth: 1,
        borderColor: palette.light_grey,
        maxHeight: 200,
      }}
    />
  );
};

const VoicemailBubble = ({ voicemail }) => {
  return (
    <View
      style={{
        width: "100%",
        justifyContent: "center",
        alignContent: "center",
        alignItems: "center",
        flexDirection: "row",
        marginTop: 8,
        zIndex: 100,
      }}
    >
      <ReactAudioPlayer
        src={voicemail.url}
        controls
        controlsList="nodownload noplaybackrate"
      />
    </View>
  );
};

const renderSystemMessage = (props) => {
  switch (props.currentMessage.interactable_type) {
    case "TwilioCall": {
      const timestampProps = {
        ...props,
        currentMessage: {
          ...props.currentMessage,
          text: dayjs(props.currentMessage.createdAt).format(" - h:mm A"),
        },
      };

      return (
        <SystemMessage {...props}>
          <View
            style={{
              width: "100%",
              justifyContent: "center",
              alignContent: "center",
              alignItems: "center",
              flexDirection: "row",
              marginTop: 8,
            }}
          >
            <View
              style={{
                maxHeight: 32,
                justifyContent: "flex-end",
                alignContent: "center",
                alignItems: "center",
                flexDirection: "row",
              }}
            ></View>

            <View
              style={{
                minWidth: "60%",
                flexDirection: "row",
                justifyContent: "center",
                alignContent: "center",
                alignItems: "center",
              }}
            >
              <Icon
                color="#b2b2b2"
                size={14}
                style={{ marginRight: 8 }}
                name={
                  props.currentMessage.direction.indexOf("outbound") > -1
                    ? "phone-outgoing"
                    : "phone-incoming"
                }
              />
              <SystemText {...props} />
              <SystemText {...timestampProps} />
            </View>

            <View
              style={{
                maxHeight: 32,
                justifyContent: "flex-end",
                alignContent: "center",
                alignItems: "center",
                flexDirection: "row",
              }}
            ></View>
          </View>

          {props.currentMessage?.meta?.voicemail && (
            <VoicemailBubble
              voicemail={props.currentMessage?.meta?.voicemail}
            />
          )}
        </SystemMessage>
      );
    }

    default: {
      return (
        <SystemMessage {...props}>
          <SystemText {...props} />
        </SystemMessage>
      );
    }
  }
};

const renderBubble = (props) => {
  switch (props.currentMessage.interactable_type) {
    case "TwilioCall": {
      return <CallBubble {...props} />;
    }

    default: {
      return <Bubble {...props} />;
    }
  }
};

const styles = StyleSheet.create({
  conversation_thread__text_input: {
    borderTopWidth: 0,
    backgroundColor: palette.white,
    borderRadius: 0,
  },
  conversation_bar__container: {
    height: 75,
    borderTopWidth: 0,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderColor: palette.light_grey,
    // backgroundColor: palette.lightest_grey
  },
  conversation_bar__back_btn_icon: {},
  conversation_bar__action_btn_icon: {
    marginRight: 10,
  },
  conversation_bar__back_btn_text_container: {
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
    height: "100%",
    flexDirection: "row",
  },
  conversation_bar__back_btn_text: {
    fontSize: 18,
    color: palette.success,
    fontWeight: "600",
  },
  conversation_bar__title_text: {
    fontSize: 18,
    color: palette.black,
    fontWeight: "600",
    textAlign: "center",
  },
  new_conversation_bar__container: {
    height: 75,
    borderTopWidth: 0,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderColor: palette.light_grey,
    justifyContent: "center",
  },
  new_conversation_bar__input_label: {
    fontSize: 16,
    fontWeight: "500",
    color: palette.grey,
  },
  new_conversation_bar__input: {
    border: "none",
  },
});
